import React from "react";
import {
  AlertIcon,
  BookIcon,
  CornerDownRightIcon,
  CreditCardIcon,
  ErrorIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PercentIcon,
  PersonIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  SuccessIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/financial-services.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const financialServicesCdpConfig: CdpIndustryConfig = {
  icon: MoneyIcon,
  name: "financial services",
  pluralNoun: "financial services companies",
  color: "red",
  solutionPageSlug: "/solutions/financial-services",
  seoCopy:
    "Leverage all your financial services data, including customers, accounts, transactions, risk profiles, loan applications, and payment records.",
  heroImage: {
    src: hero,
    alt: "A photograph of a person using a tablet with a list of example data tables overlayed. The data tables included are for Loan Applications, Payment Records, Customers, Accounts, and Risk Profiles.",
  },
  adoptingCdpsDescription: (
    <>
      In financial services, trust is everything. Customers have more options than ever for banking, insurance, and
      investments. {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
    {
      icon: TrendIcon,
      color: "green",
      title: "Grown LTV",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
  ],
  dataStatsTitleMaxW: "3xl",
  dataStats: [
    {
      title: "$44T",
      description: (
        <>
          Estimated market
          <br />
          cap for financial services by 2028
        </>
      ),
      href: "https://www.researchandmarkets.com/reports/5939673/financial-services-global-market-report",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "Wells Fargo analyzes billions of digital interactions to power personalized conversations for 70 million customers",
      href: "https://www.pega.com/customers/wells-fargo-customer-decision-hub",
    },
    {
      title: "Chime uses Hightouch to optimize acquisition costs and audience targeting across paid media",
      href: "/customers/chime",
    },
    {
      title: "M1 Finance’s marketing team builds audience segments using over 750 attributes",
      href: "/customers/m1finance",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer acquisition using demographic and transaction data to target potential customers and untapped lookalike audiences with promotions like no-fee accounts or higher APY savings, increasing new account sign-ups.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by using CRM data to suppress active account holders from acquisition campaigns and reallocate budget toward new prospects, improving ROAS and campaign efficiency.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who started but did not complete an application by retargeting them with incentives like fee waivers or account-opening bonuses to recover lost prospects and drive incremental account openings.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote complementary financial products to existing customers by using credit card spend data to serve personalized loan offers, increasing wallet share and fulfilling unmet financial needs.",
      },
      {
        icon: MegaphoneIcon,
        title: "Referral campaigns",
        description:
          "Leverage existing customers to attract new users by offering referral promotions that provide incentives for both the referrer and the new customer, driving cost-effective acquisition and advocacy.",
      },
      {
        icon: SuccessIcon,
        title: "Prequalification campaigns",
        description:
          "Highlight prequalified financial offers by serving personalized ads featuring credit card or mortgage rates to eligible customers, increasing application conversion rates.",
      },
      // Third row
      {
        icon: BookIcon,
        title: "Education campaigns",
        description:
          "Build trust by offering free educational resources such as financial literacy webinars to attract high-value leads, strengthening brand loyalty and acquiring informed customers.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Finance media networks",
        description:
          "Monetize customer insights and first-party data by offering anonymized audience segments to financial brand partners for targeted advertising, generating incremental revenue through premium audience data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Promote complementary financial products by using transaction data to recommend relevant services like retirement accounts or investment plans, increasing LTV.",
      },
      {
        icon: PersonWithCheckIcon,
        title: "Onboarding campaigns",
        description:
          "Encourage the completion of the initial account setup, often with incentives like waived fees or interest boosts to motivate the first use or engagement.",
      },
      {
        icon: RefreshIcon,
        title: "Account reactivation campaigns",
        description:
          "Send personalized messages reminding users to complete their account setup, creating urgency and increasing re-engagement.",
      },
      // Second row
      {
        icon: SparklesIcon,
        title: "Savings challenge campaigns",
        description:
          "Encourage recurring deposits with gamified incentives and rewards, promoting long-term financial habits.",
      },
      {
        icon: AlertIcon,
        title: "Rate change notifications",
        description:
          "Notify customers of favorable rate changes, like loan rate decreases or higher savings interest rates, prompting action.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Highlight region-specific financial services, such as mortgage offers in high-demand housing markets, increasing local engagement.",
      },
      // Third row
      {
        icon: PercentIcon,
        title: "Fee reduction campaigns",
        description:
          "Offer limited-time fee waivers to reward frequent users, boosting customer goodwill and retention.",
      },
      {
        icon: TrendIcon,
        title: "Loyalty program engagement",
        description:
          "Encourage higher spending and credit card usage by offering tiered rewards, bonus points, or exclusive perks.",
      },
      {
        icon: ErrorIcon,
        title: "Churn prevention campaigns",
        description:
          "Reduce churn by sending proactive support, reminders, or special incentives to at-risk customers.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized financial dashboards showcasing savings progress, spending trends, and relevant product suggestions to improve user engagement and drive financial decisions.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Customize app navigation by prioritizing frequently used features such as bill payments, loan calculators, or rewards tracking to enhance user satisfaction and retention.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Adjust search rankings to highlight the most relevant financial products based on user behavior, such as displaying low-interest credit cards to those exploring loan options.",
      },
      // Second row
      {
        icon: CreditCardIcon,
        title: "Recommended products",
        description:
          "Suggest financial products like CDs, investment accounts, or credit card upgrades based on user behavior and financial goals to increase product adoption.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Guide users toward high-value financial actions such as setting up auto-savings, upgrading accounts, or consolidating debt to improve financial outcomes and service utilization.",
      },
      {
        icon: AlertIcon,
        title: "Proactive bill reminders",
        description:
          "Reduce late payments by displaying reminders for upcoming or recently missed bills via dashboards and notifications, helping users avoid fees and build trust.",
      },
      // Third row
      {
        icon: MoneyIcon,
        title: "Savings goal tracking",
        description:
          "Encourage financial progress by tracking deposits and spending habits, suggesting goal timelines, and rewarding milestones with badges or incentives to boost recurring deposits.",
      },
      {
        icon: PersonIcon,
        title: "Personalized rewards tracking",
        description:
          "Promote loyalty program participation by highlighting available rewards and suggesting actions to maximize points, such as reaching spending thresholds for travel vouchers or cashback bonuses.",
      },
      {
        icon: SuccessIcon,
        title: "Pre-qualified offers",
        description:
          "Dynamically display personalized pre-approved credit card, loan, or mortgage offers within user dashboards to increase conversions and reduce application friction.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined models that may not fully capture complex account hierarchies or transactional categories",
      composable:
        "Supports tailored models for account hierarchies, transaction histories, and credit product relationships",
    },
    audienceManagement: {
      traditional:
        "Supports Supports broad segmentation but struggles with categories like customer tiers, account types, or credit scores",
      composable:
        "Enables granular segmentation using financial attributes like transaction behaviors risk scores or account balances",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like onboarding or email promotions",
      composable:
        "Powers fully adaptable journeys for financial events like loan approvals, credit upgrades, and savings goal milestones",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across credit services and banking platforms",
      composable:
        "Supports custom algorithms to unify customer profiles across accounts, credit services, and digital banking",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Prospective borrowers with incomplete applications",
    firstCategoryLabel: "Abandoned loan applications",
    secondCategoryLabel: "Pre-approved credit card recipients",
  },
};
